import {
  Select,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Label,
  ListBoxSection,
  Header,
} from "react-aria-components";
import ColorSwatch from "~/components/ColorSwatch";
import { ColorSelector } from "./index";

type Props = {
  colorSelectors: ColorSelector[];
  popularColorSelectors: ColorSelector[];
  selectedOptionId: string;
  onChange: (id: string | number) => void;
};

const ColorSelectField: React.FC<Props> = ({
  colorSelectors,
  selectedOptionId,
  onChange,
  popularColorSelectors,
}) => {
  const selectedOption = colorSelectors.find(
    (colorSelector) => colorSelector.id === selectedOptionId.toString(),
  );

  return (
    <Select
      selectedKey={selectedOptionId}
      onSelectionChange={onChange}
      className="flex w-[233px] flex-col gap-1"
      name="variant"
    >
      <Label className="text-sm font-bold text-blue900 after:content-[':']">
        Select Color
      </Label>
      <Button
        aria-label="Select Color"
        className="relative flex w-full items-center justify-between rounded-none border border-gray400 bg-white px-2 py-2"
      >
        <div className="flex items-center gap-3.5">
          {selectedOption ? (
            <>
              <ColorSwatch
                hexCodes={selectedOption.hexCodes}
                image={selectedOption.colorImage}
                isActive={false}
              />
              <span className="text-sm">{selectedOption.name}</span>
            </>
          ) : (
            <span className="text-sm">Select a color</span>
          )}
        </div>
        <span
          style={{
            backgroundImage:
              "url(data:image/jpeg;base64,iVBORw0KGgoAAAANSUhEUgAAAAkAAAAFCAQAAAA9Wg7gAAAANElEQVQI12NgCGeYiQIDGRgYGZKQBJKAfCBgYSiEChQC2VDAyVADFKhgYGdAArwMeUAMBgBFpA11yvpleAAAAABJRU5ErkJggg==)",
          }}
          className="absolute right-0 top-0 h-full w-[35px] bg-gray50 bg-center bg-no-repeat text-gray400"
          aria-hidden="true"
        />
      </Button>
      <Popover className="-mt-1.5 w-[--trigger-width]">
        <ListBox
          autoFocus={false}
          className="max-h-[300px] overflow-auto rounded-none border border-gray400 bg-white"
        >
          {popularColorSelectors.length > 0 ? (
            <ListBoxSection aria-label="Popular Colors">
              <Header className="center flex items-center justify-center gap-3 py-1 font-serif text-lg font-bold text-blue900">
                <span className="h-[1px] grow bg-black" />
                Popular Colors
                <span className="h-[1px] grow bg-black" />
              </Header>
              {popularColorSelectors.map((colorSelector) => (
                <ListBoxItem
                  key={`popular-${colorSelector.id}`}
                  id={`popular-${colorSelector.id}`}
                  textValue={colorSelector.name}
                  className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 outline-none hover:bg-gray50 focus:bg-gray50 ${
                    colorSelector.id === selectedOptionId ? "bg-gray50" : ""
                  }`}
                  aria-label={colorSelector.name}
                >
                  <ColorSwatch
                    hexCodes={colorSelector.hexCodes}
                    image={colorSelector.colorImage}
                    isActive={colorSelector.id === selectedOptionId}
                  />
                  <span className="grow text-center text-sm">
                    {colorSelector.name}
                  </span>
                </ListBoxItem>
              ))}
            </ListBoxSection>
          ) : null}
          <ListBoxSection aria-label="All Colors">
            <Header className="center flex items-center justify-center gap-3 py-1 font-serif text-lg font-bold text-blue900">
              <span className="h-[1px] grow bg-black" />
              All Colors
              <span className="h-[1px] grow bg-black" />
            </Header>
            {colorSelectors.map((colorSelector) => (
              <ListBoxItem
                key={colorSelector.id}
                id={colorSelector.id}
                textValue={colorSelector.name}
                className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 outline-none hover:bg-gray50 focus:bg-gray50 ${
                  colorSelector.id === selectedOptionId ? "bg-gray50" : ""
                }`}
                aria-label={colorSelector.name}
              >
                <ColorSwatch
                  hexCodes={colorSelector.hexCodes}
                  image={colorSelector.colorImage}
                  isActive={colorSelector.id === selectedOptionId}
                />
                <span className="grow text-center text-sm">
                  {colorSelector.name}
                </span>
              </ListBoxItem>
            ))}
          </ListBoxSection>
        </ListBox>
      </Popover>
    </Select>
  );
};

export default ColorSelectField;
